/**
 * Web Audio sine-tone cues (BUILD-SPEC.md §7):
 *   hand-off  880Hz / 0.12s
 *   limit     660Hz / 0.25s   (remaining crosses zero)
 *   grace     420Hz / 0.5s    (remaining crosses -graceSeconds)
 *   finish    520Hz / 0.3s
 *
 * Rules:
 * - Default OFF. A visible per-screen toggle turns it on (`setEnabled`).
 * - Each turn fires the limit/grace tones AT MOST ONCE each, tracked with a
 *   0 -> 1 -> 2 stage counter that resets on hand-off (`handOff()` /
 *   `resetForNewTurn()`). Never fires on plain ticking.
 * - The AudioContext is unlocked on the first user click (`unlock()`) —
 *   required for browser tabs; Teams desktop allows autoplay anyway.
 */

type Stage = 0 | 1 | 2;

export class SoundEngine {
  private ctx: AudioContext | null = null;
  private stage: Stage = 0;
  private _enabled = false;

  get enabled(): boolean {
    return this._enabled;
  }

  setEnabled(on: boolean): void {
    this._enabled = on;
  }

  /** Call from a user gesture (e.g. the first click anywhere in the app) to
   * satisfy autoplay policies. Safe to call more than once. */
  unlock(): void {
    if (this.ctx) return;
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    try {
      this.ctx = new Ctor();
      // A ~0 duration silent blip nudges some browsers into the "unlocked" state.
      this.playTone(0, 0.001, 0);
    } catch {
      this.ctx = null;
    }
  }

  /** Reset the once-per-turn stage counter. Call on every hand-off
   * (start/next), before playing the hand-off tone. */
  resetForNewTurn(): void {
    this.stage = 0;
  }

  handOff(): void {
    this.resetForNewTurn();
    this.playTone(880, 0.12);
  }

  finish(): void {
    this.playTone(520, 0.3);
  }

  /**
   * Called on every render/tick with the current remaining seconds for the
   * live speaker. Plays the limit tone once when `remaining` first crosses
   * zero, and the grace tone once when it first crosses `-graceSeconds`.
   * Idempotent otherwise — safe to call every tick without re-triggering.
   */
  checkOvertime(remainingSeconds: number, graceSeconds: number): void {
    if (this.stage < 1 && remainingSeconds <= 0) {
      this.stage = 1;
      this.playTone(660, 0.25);
    }
    if (this.stage < 2 && remainingSeconds <= -graceSeconds) {
      this.stage = 2;
      this.playTone(420, 0.5);
    }
  }

  private playTone(freq: number, durationSeconds: number, gainPeak = 0.2): void {
    if (!this._enabled || !this.ctx) return;
    const ctx = this.ctx;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = freq;
    const t0 = ctx.currentTime;
    gain.gain.setValueAtTime(gainPeak, t0);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + Math.max(durationSeconds, 0.01));
    osc.connect(gain).connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + Math.max(durationSeconds, 0.01));
  }
}

/** One shared instance for the whole app — sound state (enabled + stage
 * counter) is a single per-tab concern, not per-component. */
export const sound = new SoundEngine();
